import { useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Eye, LogOut } from "lucide-react";
import { Sidebar, Topbar, MobileNav } from "./Nav";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const EMPLOYEE_ALLOWED = ["/app/report", "/app/resources"];

export default function AppLayout() {
  const { user, org, loading, stopImpersonate } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  if (loading)
    return <div className="flex h-screen items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;
  if (!user) return <Navigate to="/login" replace />;

  if (user.role === "employee" && !EMPLOYEE_ALLOWED.includes(location.pathname))
    return <Navigate to="/app/report" replace />;

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <MobileNav open={mobileOpen} onClose={() => setMobileOpen(false)} />
      <div className="flex flex-1 flex-col overflow-hidden">
        {user.impersonating && (
          <div className="flex items-center justify-between gap-2 bg-[hsl(var(--accent))] px-4 py-2 text-xs font-medium text-white lg:px-8" data-testid="impersonation-banner">
            <div className="flex items-center gap-2"><Eye className="h-4 w-4" /> Viewing as client workspace: <strong>{org?.name}</strong></div>
            <Button size="sm" variant="secondary" className="h-7 rounded-full text-xs" onClick={stopImpersonate} data-testid="return-to-master">
              <LogOut className="mr-1 h-3 w-3" /> Return to master
            </Button>
          </div>
        )}
        <Topbar onMenu={() => setMobileOpen(true)} />
        <main className="flex-1 overflow-y-auto">
          <motion.div key={window.location.pathname} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="mx-auto max-w-[1400px] px-4 py-6 lg:px-8 lg:py-8">
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}
